import { useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { styled, Button } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import {
  OnboardingCustomRequestType,
  OnboardingCustomRequestWithPreviewUrls,
} from 'further-types/lp/onboarding-custom-request';
import { DeepPartial } from 'further-types/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import RequestForm, { type CustomRequest } from './RequestForm';
import Request from './Request';
import DraggableList from 'components/DraggableList';
import { OnboardingSettingsTab } from '../../types';

const DraggableRequests = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

type Props = {
  customRequests: Array<OnboardingCustomRequestWithPreviewUrls>;
  handleCustomRequestsOrderChange: (orderedSteps: Array<string>) => void;
  addRequest: (values: CustomRequest, onSuccess?: () => void) => void;
  updateRequest: (
    id: string,
    values: CustomRequest,
    onSuccess: () => void,
  ) => void;
  deleteRequest: (id: string) => void;
  isLoading: boolean;
};

const CustomRequestForm: React.FC<Props> = ({
  customRequests,
  handleCustomRequestsOrderChange,
  addRequest,
  updateRequest,
  deleteRequest,
  isLoading,
}) => {
  const { selectedVehicleId: vehicleId } = useLayoutContext();
  const [currentOnboardingSettingsTab] = useLocalStorage<OnboardingSettingsTab>(
    `current-onboarding-settings-tab-${vehicleId}`,
    OnboardingSettingsTab.InvestorAndReviewSettings,
  );
  const draftKey = `draft-custom-request-${currentOnboardingSettingsTab}`;
  const [draftCustomRequest, setDraftCustomRequest] = useLocalStorage<
    DeepPartial<CustomRequest> | undefined
  >(draftKey, undefined);
  const [newRequestFormOpen, setNewRequestFormOpen] = useState(
    !!draftCustomRequest,
  );
  const [editedRequestId, setEditedRequestId] = useState<string | null>(null);

  const handleAddRequest = (request: CustomRequest) => {
    if (isLoading) {
      return;
    }

    addRequest(request, () => {
      setNewRequestFormOpen(false);
    });
  };

  const handleUpdateRequest = (request: CustomRequest) => {
    if (!editedRequestId || isLoading) {
      return;
    }

    updateRequest(editedRequestId, request, () => {
      setEditedRequestId(null);
    });
  };

  const handleDeleteRequest = (id: string) => {
    if (isLoading) {
      return;
    }

    deleteRequest(id);
  };

  const handleShowEditForm = (id: string) => {
    setNewRequestFormOpen(false);
    setEditedRequestId(id);
  };

  const handleShowAddForm = () => {
    setEditedRequestId(null);
    setNewRequestFormOpen(true);
  };

  //TODO: Add section title support
  const nonSectionRequests = customRequests.filter(
    (request) => request.type !== OnboardingCustomRequestType.SectionTitle,
  );

  const handleReorder = ({
    orderedItemIds,
  }: {
    orderedItemIds: Array<string>;
  }) => {
    handleCustomRequestsOrderChange(orderedItemIds);
  };

  return (
    <Column fullWidth spacing="lg">
      <DraggableRequests
        onReorder={handleReorder}
        disabled={nonSectionRequests.length === 0}
      >
        {nonSectionRequests.map((request) => (
          <DraggableList.Item id={request.id} key={request.id} withDragHandle>
            {request.id === editedRequestId ? (
              <RequestForm
                key={request.id}
                onSave={handleUpdateRequest}
                onClose={() => {
                  setEditedRequestId(null);
                }}
                isSubmitting={isLoading}
                customRequest={request}
                setDraftCustomRequest={setDraftCustomRequest}
              />
            ) : (
              <Request
                key={request.id}
                id={request.id}
                title={request.title}
                description={request.description}
                type={request.type}
                documents={request.documents}
                isRequired={request.isRequired}
                onEdit={handleShowEditForm}
                onDelete={handleDeleteRequest}
                isLoading={isLoading}
              />
            )}
          </DraggableList.Item>
        ))}
      </DraggableRequests>

      {newRequestFormOpen ? (
        <RequestForm
          onSave={handleAddRequest}
          onClose={() => {
            setNewRequestFormOpen(false);
          }}
          isSubmitting={isLoading}
          customRequest={draftCustomRequest}
          setDraftCustomRequest={setDraftCustomRequest}
        />
      ) : null}

      <Row centered fullWidth>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={handleShowAddForm}
        >
          Add request
        </Button>
      </Row>
    </Column>
  );
};

export default CustomRequestForm;
